import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, DollarSign, Heart, Sparkles, Users, Mountain, User } from "lucide-react";

const steps = [
  { key: "intro", label: "Welcome" },
  { key: "financial", label: "Financial", icon: DollarSign, color: "#3e6b52" },
  { key: "health", label: "Health", icon: Heart, color: "#b94e4e" },
  { key: "spirituality", label: "Spirituality", icon: Sparkles, color: "#7a5fb0" },
  { key: "relationships", label: "Relationships", icon: Users, color: "#c18848" },
  { key: "endgame", label: "Endgame", icon: Mountain, color: "#486a8f" },
  { key: "coach", label: "Your coach", icon: User, color: "#486a8f" },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [stepIdx, setStepIdx] = useState(0);
  const [saving, setSaving] = useState(false);

  const [data, setData] = useState({
    financial: { current_situation: "", income_goal: "", path: "investing", top_concern: "" },
    health: { current_diet: "", diet_interest: "", exercise_level: "low", sleep_hours: "" },
    spirituality: { practice: "", daily_meditation: "no", prayer: "no", service: "" },
    relationships: { status: "single", focus: "", improvement: "" },
    endgame: { big_picture: "", timeline: "5_years", why: "" },
    coach_style: "calm",
    coach_gender: "female",
    coach_accent: "american",
    personality: "",
  });

  const step = steps[stepIdx];
  const isFirst = stepIdx === 0;
  const isLast = stepIdx === steps.length - 1;

  const update = (pillar, field, value) => {
    setData((d) => ({ ...d, [pillar]: { ...d[pillar], [field]: value } }));
  };

  const next = () => setStepIdx((i) => Math.min(i + 1, steps.length - 1));
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/onboarding`, data);
      await checkAuth();
      toast.success("Your snapshot is saved. Generating your roadmap…");
      // Kick off roadmap generation
      try {
        await axios.post(`${API}/roadmap/generate`, {});
      } catch (e) {
        console.warn("Roadmap gen failed", e);
      }
      navigate("/dashboard");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f3ea]">
      <header className="px-6 py-6 max-w-4xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-1">
          <span className="font-display text-[20px] font-semibold text-[#0f1724]">ascend</span>
          <span className="font-display text-[20px] font-semibold text-[#486a8f]">higher</span>
        </div>
        <p className="font-mono-eyebrow text-[#6b7280]">
          {stepIdx + 1} / {steps.length}
        </p>
      </header>

      {/* Progress bar */}
      <div className="max-w-4xl mx-auto px-6">
        <div className="h-1 bg-[#e6dcc6] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#486a8f] transition-all duration-500"
            style={{ width: `${((stepIdx + 1) / steps.length) * 100}%` }}
          />
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-6 py-12 md:py-16">
        {/* Intro */}
        {step.key === "intro" && (
          <div className="text-center">
            <h1 className="font-display text-[40px] md:text-[56px] leading-[1.05] text-[#0f1724] tracking-tight">
              Let's build your{" "}
              <span className="italic font-light text-[#486a8f]">snapshot</span>.
            </h1>
            <p className="font-body text-[16px] md:text-[17px] text-[#4b5563] mt-6 leading-relaxed">
              Hi {user?.name?.split(" ")[0] || "there"}. We'll ask a few honest questions across five pillars:
              financial, health, spirituality, relationships, and your endgame. This takes about 3 minutes.
              Your coach will use this to build a plan that fits <em>you</em>.
            </p>
            <div className="mt-10 grid grid-cols-5 gap-3 max-w-md mx-auto">
              {steps.slice(1, 6).map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.key} className="flex flex-col items-center">
                    <div
                      className="w-12 h-12 rounded-xl bg-white border border-[#e6dcc6] flex items-center justify-center mb-2"
                    >
                      <Icon size={18} style={{ color: s.color }} />
                    </div>
                    <p className="font-body text-[11px] text-[#6b7280]">{s.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Financial */}
        {step.key === "financial" && (
          <StepSection icon={DollarSign} color="#3e6b52" title="Financial Stability" eyebrow="Pillar 1 — Wealth with intention">
            <FieldLabel>Where are you financially right now?</FieldLabel>
            <Textarea value={data.financial.current_situation} onChange={(e) => update("financial", "current_situation", e.target.value)} placeholder="Be honest. Debts, savings, income, stress level…" className="resize-none min-h-[90px]" />

            <FieldLabel>What's your income / wealth goal?</FieldLabel>
            <Input value={data.financial.income_goal} onChange={(e) => update("financial", "income_goal", e.target.value)} placeholder="e.g. $10k/mo, $1M net worth, financial freedom…" />

            <FieldLabel>Which path fits you best?</FieldLabel>
            <RadioGroup value={data.financial.path} onValueChange={(v) => update("financial", "path", v)} className="grid grid-cols-2 gap-3">
              {[{ v: "investing", l: "Investing" }, { v: "business", l: "Business" }, { v: "creative_financing", l: "Creative financing" }, { v: "career", l: "Career growth" }].map((o) => (
                <label key={o.v} className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${data.financial.path === o.v ? "bg-[#e9f0ec] border-[#3e6b52]" : "bg-white border-[#e6dcc6] hover:bg-[#faf6ec]"}`}>
                  <RadioGroupItem value={o.v} />
                  <span className="font-body text-sm text-[#2b3442]">{o.l}</span>
                </label>
              ))}
            </RadioGroup>

            <FieldLabel>Top concern or stuck point?</FieldLabel>
            <Input value={data.financial.top_concern} onChange={(e) => update("financial", "top_concern", e.target.value)} placeholder="e.g. can't save, no clear plan, fear of risk…" />
          </StepSection>
        )}

        {/* Health */}
        {step.key === "health" && (
          <StepSection icon={Heart} color="#b94e4e" title="Health" eyebrow="Pillar 2 — Daily habits that fit you">
            <FieldLabel>How are you eating today?</FieldLabel>
            <Input value={data.health.current_diet} onChange={(e) => update("health", "current_diet", e.target.value)} placeholder="e.g. standard American, plant-based, no structure…" />

            <FieldLabel>Any diet you're drawn to test?</FieldLabel>
            <RadioGroup value={data.health.diet_interest} onValueChange={(v) => update("health", "diet_interest", v)} className="grid grid-cols-2 gap-3">
              {["Carnivore", "Mediterranean", "Paleo", "Whole-foods", "Not sure"].map((o) => (
                <label key={o} className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${data.health.diet_interest === o ? "bg-[#f5e6e6] border-[#b94e4e]" : "bg-white border-[#e6dcc6] hover:bg-[#faf6ec]"}`}>
                  <RadioGroupItem value={o} />
                  <span className="font-body text-sm text-[#2b3442]">{o}</span>
                </label>
              ))}
            </RadioGroup>

            <FieldLabel>Current exercise level?</FieldLabel>
            <RadioGroup value={data.health.exercise_level} onValueChange={(v) => update("health", "exercise_level", v)} className="grid grid-cols-3 gap-3">
              {[{ v: "low", l: "Low" }, { v: "moderate", l: "Moderate" }, { v: "high", l: "High" }].map((o) => (
                <label key={o.v} className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${data.health.exercise_level === o.v ? "bg-[#f5e6e6] border-[#b94e4e]" : "bg-white border-[#e6dcc6] hover:bg-[#faf6ec]"}`}>
                  <RadioGroupItem value={o.v} />
                  <span className="font-body text-sm text-[#2b3442]">{o.l}</span>
                </label>
              ))}
            </RadioGroup>

            <FieldLabel>Hours of sleep per night?</FieldLabel>
            <Input value={data.health.sleep_hours} onChange={(e) => update("health", "sleep_hours", e.target.value)} placeholder="e.g. 6, 7-8" />
          </StepSection>
        )}

        {/* Spirituality */}
        {step.key === "spirituality" && (
          <StepSection icon={Sparkles} color="#7a5fb0" title="Spirituality" eyebrow="Pillar 3 — Clarity. Presence. Service.">
            <FieldLabel>What's your spiritual practice today (if any)?</FieldLabel>
            <Input value={data.spirituality.practice} onChange={(e) => update("spirituality", "practice", e.target.value)} placeholder="e.g. Christian, agnostic, meditator, nothing…" />

            <FieldLabel>Do you meditate or reflect daily?</FieldLabel>
            <RadioGroup value={data.spirituality.daily_meditation} onValueChange={(v) => update("spirituality", "daily_meditation", v)} className="grid grid-cols-2 gap-3">
              {[{ v: "yes", l: "Yes" }, { v: "no", l: "No" }].map((o) => (
                <label key={o.v} className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${data.spirituality.daily_meditation === o.v ? "bg-[#ede8f5] border-[#7a5fb0]" : "bg-white border-[#e6dcc6]"}`}>
                  <RadioGroupItem value={o.v} />
                  <span className="font-body text-sm text-[#2b3442]">{o.l}</span>
                </label>
              ))}
            </RadioGroup>

            <FieldLabel>Do you pray?</FieldLabel>
            <RadioGroup value={data.spirituality.prayer} onValueChange={(v) => update("spirituality", "prayer", v)} className="grid grid-cols-2 gap-3">
              {[{ v: "yes", l: "Yes" }, { v: "no", l: "Not currently" }].map((o) => (
                <label key={o.v} className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${data.spirituality.prayer === o.v ? "bg-[#ede8f5] border-[#7a5fb0]" : "bg-white border-[#e6dcc6]"}`}>
                  <RadioGroupItem value={o.v} />
                  <span className="font-body text-sm text-[#2b3442]">{o.l}</span>
                </label>
              ))}
            </RadioGroup>

            <FieldLabel>How do you (or would you like to) serve others?</FieldLabel>
            <Input value={data.spirituality.service} onChange={(e) => update("spirituality", "service", e.target.value)} placeholder="e.g. mentoring, volunteering, small daily acts…" />
          </StepSection>
        )}

        {/* Relationships */}
        {step.key === "relationships" && (
          <StepSection icon={Users} color="#c18848" title="Relationships" eyebrow="Pillar 4 — Love on purpose">
            <FieldLabel>Relationship status</FieldLabel>
            <RadioGroup value={data.relationships.status} onValueChange={(v) => update("relationships", "status", v)} className="grid grid-cols-2 gap-3">
              {[{ v: "single", l: "Single" }, { v: "dating", l: "Dating" }, { v: "committed", l: "Committed" }, { v: "complicated", l: "It's complicated" }].map((o) => (
                <label key={o.v} className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${data.relationships.status === o.v ? "bg-[#f5ece0] border-[#c18848]" : "bg-white border-[#e6dcc6]"}`}>
                  <RadioGroupItem value={o.v} />
                  <span className="font-body text-sm text-[#2b3442]">{o.l}</span>
                </label>
              ))}
            </RadioGroup>

            <FieldLabel>What do you most want to improve in how you love / show up?</FieldLabel>
            <Textarea value={data.relationships.focus} onChange={(e) => update("relationships", "focus", e.target.value)} placeholder="Be specific — patience, presence, communication, boundaries…" className="resize-none min-h-[80px]" />

            <FieldLabel>One thing you could do to make their (or a future partner's) life better?</FieldLabel>
            <Input value={data.relationships.improvement} onChange={(e) => update("relationships", "improvement", e.target.value)} placeholder="" />
          </StepSection>
        )}

        {/* Endgame */}
        {step.key === "endgame" && (
          <StepSection icon={Mountain} color="#486a8f" title="Endgame" eyebrow="Pillar 5 — Your ultimate picture">
            <FieldLabel>What is your big picture — the life you're ultimately building?</FieldLabel>
            <Textarea value={data.endgame.big_picture} onChange={(e) => update("endgame", "big_picture", e.target.value)} placeholder="Describe it like you're already living it…" className="resize-none min-h-[100px]" />

            <FieldLabel>Timeline</FieldLabel>
            <RadioGroup value={data.endgame.timeline} onValueChange={(v) => update("endgame", "timeline", v)} className="grid grid-cols-3 gap-3">
              {[{ v: "1_year", l: "1 year" }, { v: "5_years", l: "5 years" }, { v: "10_plus", l: "10+ years" }].map((o) => (
                <label key={o.v} className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${data.endgame.timeline === o.v ? "bg-[#e7edf5] border-[#486a8f]" : "bg-white border-[#e6dcc6]"}`}>
                  <RadioGroupItem value={o.v} />
                  <span className="font-body text-sm text-[#2b3442]">{o.l}</span>
                </label>
              ))}
            </RadioGroup>

            <FieldLabel>Why does this big picture matter to you?</FieldLabel>
            <Textarea value={data.endgame.why} onChange={(e) => update("endgame", "why", e.target.value)} placeholder="The deeper why — family, freedom, legacy, meaning…" className="resize-none min-h-[80px]" />
          </StepSection>
        )}

        {/* Coach */}
        {step.key === "coach" && (
          <StepSection icon={User} color="#486a8f" title="Design your coach" eyebrow="Your mentor, your way">
            <FieldLabel>What coaching style fits you best?</FieldLabel>
            <div className="grid grid-cols-2 gap-3">
              {[
                { v: "hard", l: "Hard & Straight", d: "Tough love. No fluff." },
                { v: "calm", l: "Calm & Collected", d: "Thoughtful, slow, clarifying." },
                { v: "nobs", l: "No-BS Mentor", d: "Brutally honest. Fast." },
                { v: "friend", l: "Best Friend", d: "Warm, casual, grounded." },
              ].map((o) => (
                <button
                  type="button"
                  key={o.v}
                  onClick={() => setData((d) => ({ ...d, coach_style: o.v }))}
                  className={`text-left px-4 py-3 rounded-xl border transition-colors ${data.coach_style === o.v ? "bg-[#e7edf5] border-[#486a8f]" : "bg-white border-[#e6dcc6] hover:bg-[#faf6ec]"}`}
                >
                  <p className="font-body font-medium text-sm text-[#0f1724]">{o.l}</p>
                  <p className="font-body text-[12px] text-[#6b7280] mt-1">{o.d}</p>
                </button>
              ))}
            </div>

            <FieldLabel>Coach voice</FieldLabel>
            <div className="grid grid-cols-2 gap-3">
              {[{ v: "female", l: "Female" }, { v: "male", l: "Male" }].map((o) => (
                <button
                  type="button"
                  key={o.v}
                  onClick={() => setData((d) => ({ ...d, coach_gender: o.v }))}
                  className={`px-4 py-3 rounded-xl border font-body text-sm transition-colors ${data.coach_gender === o.v ? "bg-[#e7edf5] border-[#486a8f] text-[#0f1724]" : "bg-white border-[#e6dcc6] text-[#2b3442] hover:bg-[#faf6ec]"}`}
                >
                  {o.l}
                </button>
              ))}
            </div>

            <FieldLabel>Accent</FieldLabel>
            <div className="grid grid-cols-2 gap-3">
              {[{ v: "american", l: "American" }, { v: "british", l: "British" }].map((o) => (
                <button
                  type="button"
                  key={o.v}
                  onClick={() => setData((d) => ({ ...d, coach_accent: o.v }))}
                  className={`px-4 py-3 rounded-xl border font-body text-sm transition-colors ${data.coach_accent === o.v ? "bg-[#e7edf5] border-[#486a8f] text-[#0f1724]" : "bg-white border-[#e6dcc6] text-[#2b3442] hover:bg-[#faf6ec]"}`}
                >
                  {o.l}
                </button>
              ))}
            </div>

            <FieldLabel>Anything your coach should know about your personality?</FieldLabel>
            <Textarea value={data.personality} onChange={(e) => setData((d) => ({ ...d, personality: e.target.value }))} placeholder="e.g. I'm an introvert, I respond to direct challenge, I've been through hard things…" className="resize-none min-h-[80px]" />
          </StepSection>
        )}

        {/* Nav */}
        <div className="mt-12 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={back}
            disabled={isFirst}
            className="rounded-full font-body text-[14px] text-[#4b5563] hover:bg-[#f2ecdc] disabled:opacity-0"
          >
            <ArrowLeft size={16} className="mr-1" />
            Back
          </Button>
          {!isLast ? (
            <Button
              onClick={next}
              className="h-12 px-7 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white font-medium btn-lift"
            >
              Continue <ArrowRight size={16} className="ml-2" />
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={saving}
              className="h-12 px-7 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white font-medium btn-lift"
            >
              {saving ? "Saving…" : "Complete snapshot"} <Check size={16} className="ml-2" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}

function FieldLabel({ children }) {
  return <Label className="font-mono-eyebrow text-[#486a8f] mt-7 block">{children}</Label>;
}

function StepSection({ icon: Icon, color, title, eyebrow, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: `${color}18` }}
        >
          <Icon size={18} style={{ color }} />
        </div>
        <p className="font-mono-eyebrow" style={{ color }}>{eyebrow}</p>
      </div>
      <h2 className="font-display text-[32px] md:text-[42px] leading-[1.05] text-[#0f1724] tracking-tight mb-2">
        {title}
      </h2>
      <div className="mt-4 space-y-3">{children}</div>
    </div>
  );
}
